<template>
  <table>
    <thead v-if="showHead">
      <tr>
        <th class="rb-diamond-select" v-if="selectors"></th>
        <th>Shape</th>
        <th>
          <div class="rb-sorter-column">
            Carat
            <sorter label="Carat" type="size" :mode="'asc'" :active="false" />
          </div>
        </th>
        <th>
          <div class="rb-sorter-column">
            Color
            <sorter label="Color" type="color" :mode="'asc'" :active="false" />
          </div>
        </th>
        <th>
          <div class="rb-sorter-column">
            Clarity
            <sorter
              label="Clarity"
              type="clarity"
              :mode="'asc'"
              :active="false"
            />
          </div>
        </th>
        <th>
          <div class="rb-sorter-column">
            <span class="medium--hide small--hide"
              >{{ combinePrice ? "Total" : "Diamond" }}&nbsp;</span
            >Price
            <sorter
              label="Price"
              type="retail_price"
              :mode="'asc'"
              :active="false"
            />
          </div>
        </th>
        <th class="text-right">
          <span class="medium--hide small--hide">Video</span>
        </th>
        <th></th>
      </tr>
    </thead>
    <tbody>
      <template v-for="diamond in diamondsData.diamonds">
        <tr :key="`tr-${diamond.id}`" class="tr-table">
          <td class="rb-diamond-select" v-if="selectors">
            <custom-checkbox
              :selected="isDiamondSelected(selectedOptions, diamond)"
              :diamond="diamond"
              @rb-selected="
                () => {
                  onDiamondSelected(diamond);
                }
              "
              @rb-deselected="onDiamondDeselected"
            />
          </td>
          <td
            :style="colWidth"
            class="rb-diamond-shape"
            @click.prevent="toggleDiamondInformation(diamond.id)"
          >
            {{ diamond.shape }}
          </td>
          <td
            :style="colWidth"
            @click.prevent="toggleDiamondInformation(diamond.id)"
          >
            {{ diamond.size | carats }}
          </td>
          <td
            :style="colWidth"
            @click.prevent="toggleDiamondInformation(diamond.id)"
          >
            {{ diamond.color }}
          </td>
          <td
            :style="colWidth"
            @click.prevent="toggleDiamondInformation(diamond.id)"
          >
            {{ diamond.clarity }}
          </td>
          <td
            :style="colWidth"
            @click.prevent="toggleDiamondInformation(diamond.id)"
          >
            {{
              (combinePrice
                ? parseInt(diamond.retail_price) +
                  parseInt(dataPrice.baseRingPrice)
                : parseInt(diamond.retail_price)) | money
            }}
          </td>
          <td class="rb-diamond-options">
            <div>
              <button
                class="rb-btn-option"
                aria-label="Expand/Collapse Diamond Information"
                :id="`btn-expand-diamond-${diamond.cert_num}`"
                @click.prevent="toggleDiamondInformation($event, diamond.id)"
              >
                <icon-diamond-expand />
              </button>
            </div>
          </td>
          <td class="rb-diamond-options">
            <div>
              <button
                class="rb-btn-option rb-btn-fav"
                :id="`btn-add-to-favorites-${diamond.cert_num}`"
                aria-label="Add to Favorites"
                @click.prevent="saveFavorites($event, diamond)"
              >
                <template v-if="isFaved(diamond)">
                  <icon-heart-full />
                </template>
                <template v-else>
                  <icon-heart />
                </template>
              </button>
            </div>
          </td>
        </tr>
        <tr
          :key="`tr-expanded-${diamond.id}`"
          v-if="expanded.find((d) => d === diamond.id)"
        >
          <td colspan="8">
            <div class="rb-diamond-expanded">
              <div class="rb-diamond-image">
                <v-360
                  :diamond="diamond"
                  mode="video"
                  :api="api.visionViewer"
                  :favorites="favorites"
                ></v-360>
              </div>
              <div class="rb-diamond-data">
                <diamond-card
                  :data="data"
                  :diamond="diamond"
                  :selected-options="selectedOptions"
                  :selectors="selectors"
                  :data-price="dataPrice"
                  :favorites="favorites"
                  :combine-price="combinePrice"
                />
              </div>
            </div>
          </td>
        </tr>
      </template>
    </tbody>
  </table>
</template>
<script>
/**
 * Displays a list of diamonds as a table w/expandable cards
 *
 * @props
 * api: Object
 * selectors: Boolean
 * dataPrice: Object
 * searchDiamondsData: Object
 * showHead: Boolean
 *
 * @implements
 * ChildMixin
 * DiamondsMixin
 * FormattersMixin
 * CommMixin
 * FavsMixin
 * TrackMixin
 *
 * @emits
 * "rb-step-complete"
 * "rb-step-incomplete"
 */
import TrackMixin from "@/mixins/TrackMixin";
import CommMixin from "@/mixins/CommMixin";
import ChildMixin from "@/mixins/ChildMixin";
import DiamondsMixin from "@/mixins/DiamondsMixin";
import FormattersMixin from "@/mixins/FormattersMixin";
import FavsMixin from "@/mixins/FavsMixin";
import CustomCheckbox from "@/components/CustomCheckbox";
import DiamondCard from "@/components/DiamondCard";
import V360 from "@/components/V360";
import Sorter from "@/components/Sorter";
import IconDiamondExpand from "@/components/icons/IconDiamondExpand";
import IconHeartFull from "@/components/icons/IconHeartFull";
import IconHeart from "@/components/icons/IconHeart";
export default {
  props: {
    api: {
      type: Object,
    },
    selectors: {
      type: Boolean,
      default: true,
    },
    dataPrice: {
      type: Object,
    },
    searchDiamondsData: {
      type: Object,
    },
    showHead: {
      type: Boolean,
      default: true,
    },
    combinePrice: {
      type: Boolean,
      default: true,
    },
  },
  mixins: [
    ChildMixin,
    DiamondsMixin,
    FormattersMixin,
    CommMixin,
    FavsMixin,
    TrackMixin,
  ],
  components: {
    CustomCheckbox,
    DiamondCard,
    Sorter,
    IconDiamondExpand,
    IconHeart,
    IconHeartFull,
    V360,
  },
  data: function () {
    return {
      expanded: [],
    };
  },
  computed: {
    colWidth: function () {
      var numCols = this.selectors ? 7 : 6;
      return `width: calc(calc(100% / ${numCols}) - 220px);`;
    },
  },
  methods: {
    onDiamondSelected: function (diamond) {
      this.broadcast("rb-step-complete", { step: this.data, choice: diamond });
    },
    onDiamondDeselected: function () {
      this.broadcast("rb-step-incomplete", { step: this.data });
    },
    toggleDiamondInformation: function (event, id) {
      var diamondExpanded = this.expanded.find((d) => d === id);
      if (diamondExpanded) {
        this.expanded = this.expanded.filter((d) => d !== id);
      } else {
        this.expanded = [...this.expanded, id];
      }
      this.trackEvent(
        this.formatTracking(this.getTrackId(event), this.TRACK_CLICK),
        this.getTrackId(event)
      );
    },
  },
};
</script>
